import { useEffect, useMemo, useState } from "react";
import { useGetBeforeChatLog, useGetChatAvailableCount, useGetFreeWhetherToUse, useGetSummaryAuctionAI } from "../api";

export const AuthStatus = {
    LOADING: "LOADING",
    NOT_LOGGED_IN: "NOT_LOGGED_IN",
    ANALYSIS_IN_PROGRESS: "ANALYSIS_IN_PROGRESS",
    NEW_ITEM_ERROR: "NEW_ITEM_ERROR",
    NEEDS_FREE_TRIAL: "NEEDS_FREE_TRIAL",
    NEEDS_SUBSCRIPTION: "NEEDS_SUBSCRIPTION",
    NEEDS_CHATBOT_FREE_TRIAL: "NEEDS_CHATBOT_FREE_TRIAL",
    NEEDS_CHATBOT_SUBSCRIPTION: "NEEDS_CHATBOT_SUBSCRIPTION",
    NEEDS_CHATBOT_UPGRADE: "NEEDS_CHATBOT_UPGRADE",
    NEEDS_CHATBOT_TICKET: "NEEDS_CHATBOT_TICKET",
    NEEDS_CHATBOT_TICKET_UPGRADE: "NEEDS_CHATBOT_TICKET_UPGRADE",
    SUCCESS: "SUCCESS",
} as const;

export type Status = typeof AuthStatus[keyof typeof AuthStatus];

export const useAuthorization = ({
    missgoAIType,
    userSubs,
}: {
    missgoAIType: "default" | "chat";
    userSubs: "VVIP" | "VIP" | "BASIC" | "STANDARD" | "NONE" | "FREE_TRIAL";
}) => {
    const searchParams = new URLSearchParams(location.search);
    const token = localStorage.getItem("accessToken");
    const [isLogin, setIsLogin] = useState(false);

    useEffect(() => {
        setIsLogin(!!token);
    }, [token]);

    const {
        data: AISummaryData,
        isLoading: AISummaryDataIsLoading,
        refetch: AISummaryDataRefetch,
    } = useGetSummaryAuctionAI(searchParams.get("itemId") ?? "");
    const { data: beforeChatLogData } = useGetBeforeChatLog(searchParams.get("itemId") ?? "");
    const { data: freeWhetherToUseData, refetch: freeWhetherToUseRefetch } = useGetFreeWhetherToUse();
    const { data: chatAvailableCount, refetch: chatAvailableCountRefetch } = useGetChatAvailableCount();

    const hasError = AISummaryData?.code;
    const freeWhetherToUse = freeWhetherToUseData?.isFreeIssued; // 사용했으면 true 안했으면 false
    const ExpiredFreeTrial = freeWhetherToUseData?.isExpierd; // 만료되었으면 true 안되었으면 false
    const beforeChatLogDataLength = beforeChatLogData?.data.length === 0; // 이전 대화내용 없으면 true 있으면 false

    useEffect(() => {
        freeWhetherToUseRefetch();
        chatAvailableCountRefetch();
    }, [chatAvailableCountRefetch]);

    const status = useMemo((): Status => {
        // 로그인 안한 상태
        if (!isLogin) return AuthStatus.NOT_LOGGED_IN;

        // 로딩중
        if (AISummaryDataIsLoading) return AuthStatus.LOADING;

        // 분석 준비중
        if (hasError === 204) return AuthStatus.ANALYSIS_IN_PROGRESS;

        // 신건 7일
        if (hasError === 400) return AuthStatus.NEW_ITEM_ERROR;

        // 길잡이의 경우
        if (missgoAIType === "default") {
            if (userSubs === "VVIP" || userSubs === "VIP") {
                return AuthStatus.SUCCESS;
            }

            if (userSubs === "BASIC" || userSubs === "STANDARD" || userSubs === "NONE" || userSubs === "FREE_TRIAL") {
                // 무료체험이 사용되었고 만료되었을때
                if (freeWhetherToUse && ExpiredFreeTrial) {
                    return AuthStatus.NEEDS_SUBSCRIPTION;
                }

                // 무료체험 사용 가능
                if (!freeWhetherToUse && !ExpiredFreeTrial) {
                    return AuthStatus.NEEDS_FREE_TRIAL;
                }
            }

            if (hasError === 403) {
                return AuthStatus.NEEDS_SUBSCRIPTION;
            }
        }

        // chatAvailableCount?.remainCount = 무료체험 10회 or 300회
        // chatAvailableCount?.ticketCount = 1일 10회권 구매한 경우

        // 챗봇의 경우
        if (missgoAIType === "chat") {
            // VVIP일 경우
            if (userSubs === "VVIP") {
                // 채팅로그가 있으면
                if (!beforeChatLogDataLength) {
                    return AuthStatus.SUCCESS;
                }

                // 티켓이 있으면 성공
                if (chatAvailableCount?.remainCount > 0 || chatAvailableCount?.ticketCount > 0) {
                    return AuthStatus.SUCCESS;
                }

                // 티켓이 없으면 구매
                if (chatAvailableCount?.remainCount === 0 || chatAvailableCount?.ticketCount === 0) {
                    return AuthStatus.NEEDS_CHATBOT_TICKET;
                }
            }

            // VIP일 경우
            if (userSubs === "VIP") {
                // 무료체험이 사용 X
                if (!freeWhetherToUse) {
                    return AuthStatus.NEEDS_CHATBOT_FREE_TRIAL;
                }

                // 채팅내역이 있으면
                if (!beforeChatLogDataLength) {
                    return AuthStatus.SUCCESS;
                }

                // 무료체험이 만료됨
                if (ExpiredFreeTrial) {
                    // 티켓이 있으면 성공
                    if (chatAvailableCount?.remainCount > 0 || chatAvailableCount?.ticketCount > 0) {
                        return AuthStatus.SUCCESS;
                    }

                    // 티켓이 없으면
                    return AuthStatus.NEEDS_CHATBOT_TICKET_UPGRADE;
                }

                // 무료체험이 만료되지 않음
                if (!ExpiredFreeTrial) {
                    // 티켓이 있으면 성공
                    if (chatAvailableCount?.remainCount > 0 || chatAvailableCount?.ticketCount > 0) {
                        return AuthStatus.SUCCESS;
                    }

                    // 티켓이 없으면 구매
                    if (chatAvailableCount?.remainCount === 0 && chatAvailableCount?.ticketCount === 0) {
                        return AuthStatus.NEEDS_CHATBOT_TICKET_UPGRADE;
                    }
                }
            }

            // 짜바리 유저
            if (userSubs === "BASIC" || userSubs === "STANDARD" || userSubs === "NONE" || userSubs === "FREE_TRIAL") {
                // 무료체험이 사용 X
                if (!freeWhetherToUse) {
                    return AuthStatus.NEEDS_CHATBOT_FREE_TRIAL;
                }

                // 채팅로그가 있으면
                if (!beforeChatLogDataLength) {
                    return AuthStatus.SUCCESS;
                }

                // 무료체험이 만료됨
                if (ExpiredFreeTrial) {
                    return AuthStatus.NEEDS_CHATBOT_UPGRADE;
                }

                // 무료체험이 만료되지 않음
                if (!ExpiredFreeTrial) {
                    // 티켓이 있으면 성공
                    if (chatAvailableCount?.remainCount > 0 || chatAvailableCount?.ticketCount > 0) {
                        return AuthStatus.SUCCESS;
                    }

                    // 티켓이 없으면 구매
                    if (chatAvailableCount?.remainCount === 0 && chatAvailableCount?.ticketCount === 0) {
                        return AuthStatus.NEEDS_CHATBOT_UPGRADE;
                    }
                }

                return AuthStatus.NEEDS_CHATBOT_UPGRADE;
            }
        }

        if (AISummaryData?.summary?.data) return AuthStatus.SUCCESS;
        return AuthStatus.LOADING;
    }, [
        isLogin,
        AISummaryDataIsLoading,
        hasError,
        freeWhetherToUseData,
        freeWhetherToUse,
        AISummaryData,
        missgoAIType,
        userSubs,
        chatAvailableCount,
        beforeChatLogDataLength,
    ]);

    return { status, AISummaryData, AISummaryDataRefetch, isLogin, chatAvailableCountRefetch };
};
